// [S-061b / S-285 / S-636] Precedents — one unified retrieval surface.
//
//   - A typed query runs L2 semantic + party search (S-061b/S-636): similarity
//     cards over the tenant's own uploaded corpus. Pure L2 — there is no L1
//     side to this screen (precedents are the tenant's own data; a search
//     with no matches just means none exist yet).
//   - With the query empty, the SAME tag + structured (party / amount) filters
//     browse every indexed precedent for the organization (S-285): rich cards
//     with exclude/re-share + remove. "Clear search — browse all" drops back.
//   - Studio Vocabulary (the inferred schema) stays a separate tab.
//   Every result links to a detail view that opens the precedent's full indexed
//   clause content. There is no source PDF — the "file" is the indexed text.
//
// Tenant scope: precedents are per-studio. tenant users use their own studio
// implicitly; platform_admin (no studio of their own) picks one via the rail
// picker, mirroring /upload. Token usage only — no hardcoded colors.
import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSession } from "../auth/AuthProvider";
import {
  Alert,
  Button,
  Dialog,
  LoadingState,
  PageHead,
  Pagination,
  Select,
  Spinner,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  usePagination,
  useResponsivePageSize,
} from "../components";
import { ApiError, getApiErrorMessage } from "../data/api";
import {
  useSearchPrecedents,
  useListPrecedents,
  useRemovePrecedent,
  usePrecedentFacets,
  useStudioVocabulary,
  type PrecedentListItem,
  type PrecedentSearchResponse,
} from "../data/precedentsQuery";
import { useTenantsList } from "../data/tenantsQuery";
import {
  PrecedentVisibilityBadge,
  PrecedentVisibilityControl,
} from "./PrecedentVisibilityControl";
import type { RecordRef } from "../data/chatQuery";
import { RecordDrawerProvider, useRecordDrawer } from "./agents/RecordDrawer";

export function Precedents(): JSX.Element {
  const session = useSession();
  // Tier gate — mirrors the server-side `code: "tier_required"` 403.
  if (session !== null && session.accessTier === "basic") {
    return <UpgradeTierPrompt />;
  }
  // SCOPE, not authz: a principal with no home studio (platform staff) has no
  // studio of their own, so they pick an organization to scope to.
  const needsTenantScope = session !== null && session.studioId == null;
  return <PrecedentsHome needsTenantScope={needsTenantScope} />;
}

function PrecedentsHome({
  needsTenantScope,
}: {
  needsTenantScope: boolean;
}): JSX.Element {
  // No home studio → pick an organization to scope to. Tenant users leave this
  // null and the backend uses their own studio.
  const [pickedTenantId, setPickedTenantId] = useState<string | null>(null);
  const [tab, setTab] = useState<string>("search");
  const tenantId =
    needsTenantScope && pickedTenantId !== null ? pickedTenantId : undefined;
  const needsTenant = needsTenantScope && pickedTenantId === null;

  return (
    <RecordDrawerProvider>
      <PrecedentsPageHead />

      {needsTenantScope && (
        <PlatformAdminTenantPicker
          value={pickedTenantId}
          onChange={setPickedTenantId}
        />
      )}

      {/* [S-636] One precedents surface: search + structured browse filters in
          one place, not two tabs. A typed query runs semantic/party search; with
          the query empty, the structured filters browse the whole corpus. Studio
          Vocabulary stays a separate tab (it's the inferred schema, not a
          retrieval view). */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList ariaLabel="Precedent views" className="prec__tabs">
          <TabsTrigger value="search">Precedents</TabsTrigger>
          <TabsTrigger value="vocabulary">Studio Vocabulary</TabsTrigger>
        </TabsList>
        <TabsContent value="search">
          {needsTenant ? (
            <PickTenantPrompt />
          ) : (
            <PrecedentsExplore tenantId={tenantId} />
          )}
        </TabsContent>
        <TabsContent value="vocabulary">
          {needsTenant ? (
            <PickTenantPrompt />
          ) : (
            <StudioVocabularyTab tenantId={tenantId} />
          )}
        </TabsContent>
      </Tabs>
    </RecordDrawerProvider>
  );
}

function PlatformAdminTenantPicker({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (next: string) => void;
}): JSX.Element {
  const tenantsQuery = useTenantsList();
  const tenants = tenantsQuery.data?.tenants ?? [];
  return (
    <div className="prec__scope" data-testid="prec-tenant-picker">
      <span className="prec__scope__lbl">Viewing precedents for</span>
      {tenantsQuery.isLoading ? (
        <span className="prec__scope__hint"><Spinner size={13} /> Loading tenants…</span>
      ) : tenantsQuery.isError ? (
        <span className="prec__scope__hint">
          Couldn't load tenants: {tenantsQuery.error?.message ?? "unknown"}
        </span>
      ) : tenants.length === 0 ? (
        <span className="prec__scope__hint">No tenants provisioned yet.</span>
      ) : (
        <Select<string>
          label="Organization"
          value={value ?? undefined}
          onValueChange={(v) => v !== undefined && onChange(v)}
          clearable={false}
          placeholder="Pick an organization…"
          options={tenants.map((t) => ({
            value: t.id,
            label: t.studioId,
          }))}
        />
      )}
    </div>
  );
}

function PickTenantPrompt(): JSX.Element {
  return (
    <div className="prec__state" role="status" data-testid="pick-tenant">
      <div className="prec__state__title">Pick an organization</div>
      Choose a studio above to search or browse its precedents.
    </div>
  );
}

// [S-636] Unified precedents surface — one place to retrieve deals. A typed
// query runs semantic + party search (similarity cards); with
// the query empty, the same tag + structured filters browse the whole corpus
// (rich cards with remove/visibility). "Clear search" drops back to browse.
// Nothing from either old tab is lost.
function PrecedentsExplore({
  tenantId,
}: {
  tenantId: string | undefined;
}): JSX.Element {
  const [queryText, setQueryText] = useState("");
  // APPLIED filters — these drive the browse list + search. The search row stays
  // clean (just the query); the taxonomy tag + compensation range are edited in
  // the "More filters" modal against DRAFT state and committed on Apply. (Party
  // matching is handled by the query field, not a separate input.)
  const [dealType, setDealType] = useState<string | undefined>(undefined);
  const [guild, setGuild] = useState<string | undefined>(undefined);
  const [structured, setStructured] = useState<{
    amountMin?: number;
    amountMax?: number;
  }>({});
  // Modal DRAFT state — mirrors the applied filters while the dialog is open.
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [draftDealType, setDraftDealType] = useState<string | undefined>(undefined);
  const [draftGuild, setDraftGuild] = useState<string | undefined>(undefined);
  const [draftMin, setDraftMin] = useState("");
  const [draftMax, setDraftMax] = useState("");
  // The query currently IN EFFECT (submitted). Non-empty → search mode.
  const [submittedQuery, setSubmittedQuery] = useState("");
  const search = useSearchPrecedents();

  // [browse-paging] The browse list is paged SERVER-side: one page per request,
  // the next fetched when the reader asks for it. Pulling the whole corpus up
  // front is the slow path at studio scale, and the result cap that used to
  // stand in for paging silently hid everything past the newest 25.
  const pageSize = useResponsivePageSize({ mobile: 6, desktop: 10 });
  const [page, setPage] = useState(1);

  const list = useListPrecedents({
    limit: pageSize,
    offset: (page - 1) * pageSize,
    ...(tenantId !== undefined ? { tenantId } : {}),
    ...(dealType !== undefined ? { dealType } : {}),
    ...(guild !== undefined ? { guild } : {}),
    ...(structured.amountMin !== undefined ? { amountMin: structured.amountMin } : {}),
    ...(structured.amountMax !== undefined ? { amountMax: structured.amountMax } : {}),
  });

  const searching = submittedQuery.trim().length > 0;

  const runSearch = (e: FormEvent): void => {
    e.preventDefault();
    const q = queryText.trim();
    setSubmittedQuery(q);
    if (q.length > 0) {
      search.mutate({
        queryText: q,
        ...(dealType !== undefined ? { dealType } : {}),
        ...(guild !== undefined ? { guild } : {}),
        ...(tenantId !== undefined ? { tenantId } : {}),
      });
    }
  };
  const clearSearch = (): void => {
    setQueryText("");
    setSubmittedQuery("");
  };

  // Seed the modal's draft fields from what's applied, then open it.
  const openFilters = (): void => {
    setDraftDealType(dealType);
    setDraftGuild(guild);
    setDraftMin(structured.amountMin !== undefined ? String(structured.amountMin) : "");
    setDraftMax(structured.amountMax !== undefined ? String(structured.amountMax) : "");
    setFiltersOpen(true);
  };
  // Commit draft → applied and close.
  const applyFilters = (): void => {
    setDealType(draftDealType);
    setGuild(draftGuild);
    setStructured({
      ...(draftMin.trim().length > 0 ? { amountMin: Number(draftMin) } : {}),
      ...(draftMax.trim().length > 0 ? { amountMax: Number(draftMax) } : {}),
    });
    setFiltersOpen(false);
  };
  // Reset the modal fields (not the applied filters — that needs Apply).
  const clearDraftFilters = (): void => {
    setDraftDealType(undefined);
    setDraftGuild(undefined);
    setDraftMin("");
    setDraftMax("");
  };
  // Drop every applied filter (and the drafts) — the chip-row "Clear all".
  const clearAllFilters = (): void => {
    setDealType(undefined);
    setGuild(undefined);
    setStructured({});
    clearDraftFilters();
  };

  // Applied filters as removable chips, in display order.
  const filterChips: Array<{ key: string; axis: string; label: string; remove: () => void }> = [
    ...(dealType !== undefined
      ? [{ key: "deal", axis: "Deal", label: prettyDealType(dealType), remove: () => setDealType(undefined) }]
      : []),
    ...(guild !== undefined
      ? [{ key: "guild", axis: "Guild", label: guild, remove: () => setGuild(undefined) }]
      : []),
    ...(structured.amountMin !== undefined
      ? [{
          key: "min",
          axis: "Min",
          label: `$${structured.amountMin.toLocaleString()}`,
          remove: () => setStructured(({ amountMax }) => (amountMax !== undefined ? { amountMax } : {})),
        }]
      : []),
    ...(structured.amountMax !== undefined
      ? [{
          key: "max",
          axis: "Max",
          label: `$${structured.amountMax.toLocaleString()}`,
          remove: () => setStructured(({ amountMin }) => (amountMin !== undefined ? { amountMin } : {})),
        }]
      : []),
  ];
  const filterCount = filterChips.length;
  const hasActiveFilters = filterCount > 0;

  const rows = list.data?.precedents ?? [];
  // `total` spans every page (server-side count), so the pager is sized without
  // fetching the pages it offers.
  const total = list.data?.total ?? rows.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, total);
  // Only a cold load blanks the screen — paging keeps the previous page visible
  // (keepPreviousData) under the inline "Refreshing…" line.
  const isFetchingEmpty = list.isFetching && rows.length === 0;

  // A filter change re-defines what page 1 holds, so an offset carried over from
  // the old result set would land somewhere arbitrary. Same for a page that fell
  // off the end when the corpus (or the viewport-derived page size) changed.
  useEffect(() => {
    setPage(1);
  }, [dealType, guild, structured.amountMin, structured.amountMax, pageSize]);
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  return (
    <>
      {/* [design import: Precedent.html] Clean search row — just the query, a
          "More filters" button (badged with the applied-filter count), and
          Search. The taxonomy tag + compensation range move into a modal so the
          result cards stay above the fold; applied filters show as removable
          chips below. */}
      <form className="prec__form" onSubmit={runSearch} role="search">
        <textarea
          className="prec__query"
          rows={1}
          placeholder="Search by party name or clause — e.g. Margot Devlin, or best-of-net capped at 5%"
          value={queryText}
          onChange={(e) => setQueryText(e.target.value)}
          aria-label="Search query"
        />
        <Button
          type="button"
          variant="bare"
          className="prec__morebtn"
          onClick={openFilters}
          data-testid="prec-more-filters"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.8}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M4 6h16M7 12h10M10 18h4" />
          </svg>
          More filters
          {filterCount > 0 && (
            <span className="prec__morebtn__count">{filterCount}</span>
          )}
        </Button>
        <Button
          type="submit"
          variant="bare"
          className="prec__submit"
          disabled={queryText.trim().length === 0 || search.isPending}
        >
          {search.isPending ? "Searching…" : "Search"}
        </Button>
        {searching && (
          <Button
            type="button"
            variant="ghost"
            className="btn--s"
            onClick={clearSearch}
            data-testid="prec-clear-search"
          >
            Clear search
          </Button>
        )}
      </form>

      {filterCount > 0 && (
        <div className="prec__activefilters" data-show data-testid="prec-active-filters">
          {filterChips.map((c) => (
            <span key={c.key} className="prec__tag">
              <span className="prec__tag__axis">{c.axis}</span>
              {c.label}
              <Button
                type="button"
                variant="bare"
                className="prec__tag__x"
                onClick={c.remove}
                aria-label={`Remove ${c.axis} ${c.label} filter`}
              >
                ×
              </Button>
            </span>
          ))}
          <Button
            type="button"
            variant="bare"
            className="prec__clearall"
            onClick={clearAllFilters}
            data-testid="filter-clear"
          >
            Clear all
          </Button>
        </div>
      )}

      {/* More-filters modal — draft edits committed on Apply. */}
      <Dialog
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        contentClassName="ds-dialog__content prec__filtersmodal"
        title="More filters"
        description="Narrow by taxonomy tag or compensation range."
      >
        <div className="prec__filtersmodal__body" data-testid="prec-filters-modal">
          <div className="prec__mf">
            <label>Taxonomy tags</label>
            <TagFilter
              tenantId={tenantId}
              selected={{ dealType: draftDealType, guild: draftGuild }}
              onChange={(next) => {
                setDraftDealType(next.dealType);
                setDraftGuild(next.guild);
              }}
            />
          </div>
          <div className="prec__mf">
            <label htmlFor="prec-amt-min">Compensation range</label>
            <div className="prec__mf__row">
              <input
                id="prec-amt-min"
                className="prec__filter__amt"
                type="number"
                min={0}
                placeholder="Min $"
                aria-label="Minimum compensation"
                value={draftMin}
                onChange={(e) => setDraftMin(e.target.value)}
                data-testid="filter-amount-min"
              />
              <input
                className="prec__filter__amt"
                type="number"
                min={0}
                placeholder="Max $"
                aria-label="Maximum compensation"
                value={draftMax}
                onChange={(e) => setDraftMax(e.target.value)}
                data-testid="filter-amount-max"
              />
            </div>
          </div>
        </div>
        <div className="prec__modal__ft">
          <Button
            type="button"
            variant="bare"
            className="prec__modal__ft__txt"
            onClick={clearDraftFilters}
          >
            Clear all
          </Button>
          <Button type="button" variant="ghost" onClick={() => setFiltersOpen(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={applyFilters}
            data-testid="filter-apply"
          >
            Apply filters
          </Button>
        </div>
      </Dialog>

      {searching ? (
        search.isPending ? (
          <LoadingState label="Searching precedents…" />
        ) : search.isError ? (
          <SearchError error={search.error} />
        ) : search.data !== undefined ? (
          <SearchResults result={search.data} tenantId={tenantId} />
        ) : null
      ) : list.isLoading || isFetchingEmpty ? (
        <LoadingState label="Loading indexed precedents…" />
      ) : list.isError ? (
        <SearchError error={list.error} />
      ) : list.data !== undefined && !list.data.l2Deployed ? (
        <ScheduleL2SetupEmptyState />
      ) : rows.length > 0 ? (
        <>
          <div className="prec__results" role="list" data-testid="browse-results">
            {rows.map((p) => (
              <PrecedentBrowseCard key={p.documentId} item={p} tenantId={tenantId} />
            ))}
          </div>
          {list.isFetching && (
            <div className="ds-loading ds-loading--inline" role="status" aria-live="polite">
              <Spinner size={14} label="Refreshing precedents" />
              <span className="ds-loading__label">Refreshing…</span>
            </div>
          )}
          <Pagination
            page={page}
            pageCount={pageCount}
            onPageChange={setPage}
            total={total}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            itemLabel="precedent"
          />
        </>
      ) : hasActiveFilters ? (
        <div className="prec__state">
          <div className="prec__state__title">No precedents match</div>
          No indexed precedents match these filters. Loosen them, or ingest
          more deals to broaden the corpus.
        </div>
      ) : (
        <PrecedenceUnlockState />
      )}
    </>
  );
}

// [Phase 3] Studio Vocabulary — the classification vocabulary INFERRED from the
// studio's own corpus (no config). Shows, per axis, the distinct values the
// system has learned from the deals, with counts.
function StudioVocabularyTab({
  tenantId,
}: {
  tenantId: string | undefined;
}): JSX.Element {
  const q = useStudioVocabulary(tenantId);

  if (q.isLoading) {
    return <LoadingState label="Inferring vocabulary from your deals…" />;
  }
  if (q.isError) return <SearchError error={q.error} />;
  if (q.data !== undefined && !q.data.l2Deployed) {
    return <ScheduleL2SetupEmptyState />;
  }
  if (q.data === undefined || q.data.precedentCount === 0) {
    // [Phase 4] No corpus → the studio is L1-only; show the unlock path, not a
    // bare empty state. The vocabulary is inferred from ingested deals.
    return <PrecedenceUnlockState />;
  }

  const v = q.data;
  const axes: Array<{ key: keyof typeof v; label: string }> = [
    { key: "instrumentType", label: "Instrument types" },
    { key: "counterpartyRole", label: "Counterparty roles" },
    { key: "clauseType", label: "Clause types" },
    { key: "dealType", label: "Deal types" },
    { key: "guild", label: "Guilds" },
    { key: "tier", label: "Talent tiers" },
    { key: "budgetBand", label: "Budget bands" },
  ];

  return (
    <div className="vocab" data-testid="studio-vocabulary">
      <p className="vocab__intro">
        Inferred from <b>{v.precedentCount}</b> ingested deal
        {v.precedentCount === 1 ? "" : "s"} — no configuration. This vocabulary
        grows as you ingest more.
      </p>
      {axes.map(({ key, label }) => {
        const facets = (v[key] ?? []) as Array<{ value: string; count: number }>;
        if (facets.length === 0) return null;
        return (
          <section key={key} className="vocab__axis" aria-label={label}>
            <h3 className="vocab__axis__title">
              {label} <span className="vocab__axis__n">{facets.length}</span>
            </h3>
            <ul className="vocab__values" role="list">
              {facets.map((f) => (
                <li key={f.value} className="vocab__value" data-testid="vocab-value">
                  {prettyDealType(f.value)}
                  <span className="vocab__count">{f.count}</span>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

function SearchResults({
  result,
  tenantId,
}: {
  result: PrecedentSearchResponse;
  tenantId: string | undefined;
}): JSX.Element {
  // Hook before any early return (rules-of-hooks). Pages the result rows.
  const pager = usePagination(result.precedents, 24);
  // L2-not-deployed → schedule-setup empty state (Brief §2.5).
  if (!result.l2Deployed) {
    return <ScheduleL2SetupEmptyState />;
  }
  // Empty query result with L2 deployed → "no matches"
  if (result.precedents.length === 0) {
    return (
      <div className="prec__state">
        <div className="prec__state__title">No precedents match</div>
        Loosen the filters or try a broader query.
      </div>
    );
  }
  return (
    <>
      {/* [S-636] Same rich card as browse — search + browse look identical. */}
      <div className="prec__results" role="list" data-testid="browse-results">
        {pager.pageItems.map((p) => (
          <PrecedentBrowseCard key={p.documentId} item={p} tenantId={tenantId} />
        ))}
      </div>
      <Pagination
        page={pager.page}
        pageCount={pager.pageCount}
        onPageChange={pager.setPage}
        total={pager.total}
        rangeStart={pager.rangeStart}
        rangeEnd={pager.rangeEnd}
        itemLabel="result"
      />
    </>
  );
}

// [S-413 follow-up] Builds this precedent's RecordRef for the shared preview
// drawer — same widget Templates/Drafts open, so browsing any of the three
// feels consistent. The id stays internal (never rendered raw).
function precedentRef(item: PrecedentListItem, tenantId: string | undefined): RecordRef {
  const tid = tenantId ?? null;
  const parties = item.parties ?? [];
  const [primary, ...rest] = parties;
  const title =
    primary !== undefined
      ? rest.length > 0
        ? `${primary} ⇄ ${rest.join(", ")}`
        : primary
      : "Precedent";
  const subtitle = [
    item.dealType !== null ? prettyDealType(item.dealType) : null,
    item.guild,
  ]
    .filter((x): x is string => x !== null && x.length > 0)
    .join(" · ");
  return {
    type: "precedent",
    id: item.documentId,
    tenantId: tid,
    title,
    ...(subtitle.length > 0 ? { subtitle } : {}),
    ...(item.summary.trim().length > 0 ? { summary: item.summary } : {}),
    uri: `csai://precedent/${tid ?? "global"}/${item.documentId}`,
  };
}

function PrecedentBrowseCard({
  item,
  tenantId,
}: {
  item: PrecedentListItem;
  tenantId: string | undefined;
}): JSX.Element {
  const parties = item.parties ?? [];
  const [primaryParty, ...counterparties] = parties;
  const counterparty = counterparties.join(", ");
  const { openRecord } = useRecordDrawer();

  return (
    <div className="prec__card__wrap" role="listitem">
      <Button
        variant="bare"
        onClick={() => openRecord(precedentRef(item, tenantId))}
        className="prec__card prec__card--link prec__card--browse"
        data-testid="browse-row"
      >
        <div className="prec__card__eyebrow">
          <span className="src-dot stud" aria-hidden="true" />
          Precedent
          <span className="prec__card__eyebrow__sep" aria-hidden="true">
            ·
          </span>
          <span className="prec__card__date">{formatDate(item.uploadedAt)}</span>
        </div>

        <div className="prec__card__main">
          <div className="prec__card__lead">
            <h3 className="prec__card__title" data-testid="browse-parties">
              {primaryParty !== undefined ? (
                <>
                  {primaryParty}
                  {counterparty.length > 0 && (
                    <>
                      <span className="prec__card__vs" aria-hidden="true">
                        ⇄
                      </span>
                      {counterparty}
                    </>
                  )}
                </>
              ) : (
                "Precedent"
              )}
            </h3>
            <div className="prec__card__chips">
              {item.dimensions?.["instrumentType"] !== undefined && (
                <span
                  className="chip chip--instrument"
                  data-testid="browse-instrument"
                >
                  {prettyDealType(item.dimensions["instrumentType"])}
                </span>
              )}
              {item.dealType !== null && (
                <span className="chip">{prettyDealType(item.dealType)}</span>
              )}
              {item.guild !== null && <span className="chip">{item.guild}</span>}
              {item.tier !== null && (
                <span className="chip">{prettyDealType(item.tier)}</span>
              )}
              {/* [S-277] Visibility tier badge (non-default tiers only). */}
              <PrecedentVisibilityBadge visibility={item.visibility} />
            </div>
          </div>

          <div className="prec__card__value">
            {item.baseAmount != null ? (
              <div className="prec__card__amt">
                {formatMoney(item.baseAmount, item.currency ?? null)}
              </div>
            ) : (
              <div className="prec__card__amt prec__card__amt--na">—</div>
            )}
            <div className="prec__card__clauses">
              {item.clauseCount} clause{item.clauseCount === 1 ? "" : "s"}
            </div>
          </div>
        </div>

        <p className="prec__card__summary">{item.summary}</p>

        <footer className="prec__card__foot">
          <span className="prec__card__open" aria-hidden="true">
            Open file <span className="prec__card__open__arw">→</span>
          </span>
        </footer>
      </Button>
      <div className="prec__card__actions">
        {/* [S-277] Exclude / re-share — owner or tenant_admin only. */}
        <PrecedentVisibilityControl
          documentId={item.documentId}
          visibility={item.visibility}
          ownerUid={item.ownerUid}
          tenantId={tenantId}
        />
        <RemovePrecedentButton id={item.documentId} tenantId={tenantId} />
      </div>
    </div>
  );
}

// [S-281] De-index a precedent from the browse list. Hits the real
// DELETE /precedents/:id (review:moderate); unauthorized callers get a real
// 403 surfaced inline. The source file in GCS is retained — this removes the
// precedent from search/browse, not the audit trail.
function RemovePrecedentButton({
  id,
  tenantId,
}: {
  id: string;
  tenantId: string | undefined;
}): JSX.Element {
  const remove = useRemovePrecedent();
  const [confirming, setConfirming] = useState(false);

  // Clears any stale mutation error so a cancelled-then-reopened dialog
  // starts clean.
  const close = (): void => {
    remove.reset();
    setConfirming(false);
  };

  return (
    <div className="prec__card__remove">
      <Button
        type="button"
        variant="bare"
        className="prec__remove"
        disabled={remove.isPending}
        onClick={() => setConfirming(true)}
        data-testid="prec-remove-open"
        aria-label="Remove this precedent from the index"
      >
        <svg
          className="prec__remove__icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M3 6h18" />
          <path d="M8 6V4h8v2" />
          <path d="M6 6l1 14h10l1-14" />
          <path d="M10 11v6M14 11v6" />
        </svg>
        {remove.isPending ? "Removing…" : "Remove"}
      </Button>

      <Dialog
        open={confirming}
        onOpenChange={(isOpen) => {
          if (!isOpen) close();
        }}
        title="Remove precedent from the index?"
        description="The original file is kept for audit. This precedent will no longer appear in precedent search or browse."
      >
        {remove.isError && (
          <div
            className="prec__confirm__error"
            role="alert"
            data-testid="prec-remove-error"
          >
            {getApiErrorMessage(remove.error) ?? "Couldn't remove precedent"}
          </div>
        )}
        <div className="prec__confirm__ft">
          <Button
            variant="ghost"
            type="button"
            disabled={remove.isPending}
            onClick={close}
            data-testid="prec-remove-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            type="button"
            disabled={remove.isPending}
            data-testid="prec-remove-confirm"
            onClick={() =>
              remove.mutate(
                { id, ...(tenantId !== undefined ? { tenantId } : {}) },
                { onSuccess: () => setConfirming(false) }
              )
            }
          >
            {remove.isPending ? "Removing…" : "Remove precedent"}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}

// [Phase 4] Honest L1-only capability state (spec §7 onboarding model). The
// studio has certified templates — drafting + standard-conformance review work
// today — but no ingested deals yet, so there is no precedent corpus to search.
// We show the unlock PATH (ingest your executed deals), not fake or bare-empty
// precedence (CLAUDE.md "no fake UI"). In-app CTA to the upload portal, not an
// external setup link — this is a corpus-presence gate, not a backend gate.
function PrecedenceUnlockState(): JSX.Element {
  return (
    <div
      className="prec__state prec__state--cta"
      role="status"
      data-testid="precedence-unlock"
    >
      <div className="prec__state__title">
        Precedents unlock with your ingested deals
      </div>
      <p>
        Your certified templates already power drafting and standard-conformance
        review. Studio <strong>precedents</strong> — how your studio has actually
        handled similar clauses across past deals — appear here once you ingest
        your executed contracts. Ingest a few and they become searchable,
        comparable precedent.
      </p>
      <Link className="prec__state__cta" to="/upload">
        Ingest your deals →
      </Link>
    </div>
  );
}

function ScheduleL2SetupEmptyState(): JSX.Element {
  return (
    <div className="prec__state prec__state--cta" role="status">
      <div className="prec__state__title">
        Precedent not yet available
      </div>
      <p>
        Your tenant's Layer 2 vector index isn't deployed. Precedents
        appear here once the index is built from your prior deals.
      </p>
      <a
        className="prec__state__cta"
        href="https://contractstudio.ai/setup-layer-2"
        rel="noopener noreferrer"
        target="_blank"
      >
        Schedule Layer 2 setup →
      </a>
    </div>
  );
}

function UpgradeTierPrompt(): JSX.Element {
  const navigate = useNavigate();
  return (
    <>
      <PrecedentsPageHead />
      <div className="prec__state prec__state--cta" role="status">
        <div className="prec__state__title">
          Precedents are a Professional-tier feature
        </div>
        <p>
          Upgrade to search and browse your studio's prior deals — parties,
          amounts, and clauses across everything you've uploaded.
        </p>
        <Button
          variant="bare"
          className="prec__state__cta"
          onClick={() => navigate("/settings")}
        >
          Upgrade plan →
        </Button>
      </div>
    </>
  );
}

function SearchError({ error }: { error: Error }): JSX.Element {
  // Map API error codes to in-context empty states rather than dumping
  // a raw "API 403 /api/admin/precedents/search" string at the user.
  if (error instanceof ApiError) {
    const code = readErrorCode(error.body);
    if (code === "tier_required") {
      return <UpgradeTierPrompt />;
    }
    // [S-613] A resident precedent store that can't be reached — DISTINCT from
    // an empty corpus. Never show this as "no precedents yet" (misleading);
    // tell the user to contact their admin, calmly (role=status, not alert).
    if (code === "precedent_store_unreachable") {
      return (
        <div
          className="prec__state"
          role="status"
          data-testid="precedent-store-unreachable"
        >
          <div className="prec__state__title">Precedent store unavailable</div>
          Your studio&rsquo;s precedent store can&rsquo;t be reached right now.
          This isn&rsquo;t an empty library — please contact your workspace admin.
        </div>
      );
    }
  }
  return (
    <Alert label="Request failed" tone="error" data-testid="search-error">
      {error.message}
    </Alert>
  );
}

function readErrorCode(body: unknown): string | null {
  if (body === null || typeof body !== "object") return null;
  const code = (body as { code?: unknown }).code;
  return typeof code === "string" ? code : null;
}

function PrecedentsPageHead(): JSX.Element {
  return (
    <PageHead
      eyebrow={
        <>
          <span className="src-dot stud" aria-hidden="true" />
          Precedents
        </>
      }
      title={
        <>
          What did <em>we</em> do last time?
        </>
      }
      sub="Search or browse your studio's prior deals. Open any precedent to read its full indexed clauses."
    />
  );
}

// [S-282] One search box that autocompletes to the organization's taxonomy tags.
// The vocabulary is the dealType/guild values that actually exist in the
// tenant's indexed corpus (with counts), so every suggestion returns results —
// no wall of dropdowns, no dead-end tags. The vector filter is single-valued
// per axis (where dealType == … / guild == …), so at most one tag per axis is
// active; picking a second replaces the first.
type TagAxis = "dealType" | "guild";
type TagSelection = { dealType?: string; guild?: string };
type TagOption = { axis: TagAxis; value: string; label: string; count: number };

const axisLabel = (axis: TagAxis): string =>
  axis === "dealType" ? "Deal" : "Guild";
const tagText = (axis: TagAxis, value: string): string =>
  axis === "dealType" ? prettyDealType(value) : value;

function TagFilter({
  tenantId,
  selected,
  onChange,
}: {
  tenantId: string | undefined;
  selected: TagSelection;
  onChange: (next: TagSelection) => void;
}): JSX.Element {
  const facets = usePrecedentFacets(tenantId);
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);

  const options = useMemo<TagOption[]>(() => {
    const f = facets.data;
    if (f === undefined) return [];
    const dealType = Array.isArray(f.dealType) ? f.dealType : [];
    const guild = Array.isArray(f.guild) ? f.guild : [];
    return [
      ...dealType.map((d) => ({
        axis: "dealType" as const,
        value: d.value,
        label: prettyDealType(d.value),
        count: d.count,
      })),
      ...guild.map((g) => ({
        axis: "guild" as const,
        value: g.value,
        label: g.value,
        count: g.count,
      })),
    ];
  }, [facets.data]);

  const q = text.trim().toLowerCase();
  const suggestions = useMemo(
    () =>
      options
        .filter((o) => selected[o.axis] !== o.value)
        .filter(
          (o) =>
            q.length === 0 ||
            o.label.toLowerCase().includes(q) ||
            o.axis.toLowerCase().includes(q)
        )
        .slice(0, 8),
    [options, selected, q]
  );

  useEffect(() => {
    function onDoc(e: MouseEvent): void {
      if (
        wrapRef.current !== null &&
        !wrapRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (o: TagOption): void => {
    onChange({ ...selected, [o.axis]: o.value });
    setText("");
    setActive(0);
    setOpen(false);
  };
  const clear = (axis: TagAxis): void =>
    onChange({ ...selected, [axis]: undefined });

  const chips: TagAxis[] = [];
  if (selected.dealType !== undefined) chips.push("dealType");
  if (selected.guild !== undefined) chips.push("guild");

  return (
    <div className="prec__tagfilter" ref={wrapRef}>
      <div className="prec__tagfilter__box">
        {chips.map((axis) => {
          const value = selected[axis] as string;
          return (
            <span key={axis} className="prec__tag">
              <span className="prec__tag__axis">{axisLabel(axis)}</span>
              {tagText(axis, value)}
              <Button
                variant="bare"
                className="prec__tag__x"
                aria-label={`Remove ${tagText(axis, value)} filter`}
                onClick={() => clear(axis)}
              >
                ×
              </Button>
            </span>
          );
        })}
        <input
          className="prec__tagfilter__input"
          value={text}
          placeholder={
            chips.length === 0 ? "Filter by tag — deal type or guild…" : "Add tag…"
          }
          onChange={(e) => {
            setText(e.target.value);
            setOpen(true);
            setActive(0);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setOpen(true);
              setActive((a) => Math.min(a + 1, suggestions.length - 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setActive((a) => Math.max(a - 1, 0));
            } else if (e.key === "Enter") {
              const choice = suggestions[active];
              if (open && choice !== undefined) {
                e.preventDefault();
                pick(choice);
              }
            } else if (e.key === "Escape") {
              setOpen(false);
            } else if (
              e.key === "Backspace" &&
              text.length === 0 &&
              chips.length > 0
            ) {
              clear(chips[chips.length - 1] as TagAxis);
            }
          }}
          role="combobox"
          aria-expanded={open}
          aria-controls="prec-tag-listbox"
          aria-label="Filter precedents by taxonomy tag"
        />
      </div>
      {open && (suggestions.length > 0 || (q.length > 0 && facets.data !== undefined)) && (
        <ul
          className="prec__tagfilter__menu"
          id="prec-tag-listbox"
          role="listbox"
        >
          {suggestions.length === 0 ? (
            <li className="prec__tagopt prec__tagopt--empty">No matching tags</li>
          ) : (
            suggestions.map((o, i) => (
              <li
                key={`${o.axis}:${o.value}`}
                role="option"
                aria-selected={i === active}
                className={i === active ? "prec__tagopt is-active" : "prec__tagopt"}
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(o);
                }}
                onMouseEnter={() => setActive(i)}
              >
                <span className="prec__tagopt__axis">{axisLabel(o.axis)}</span>
                <span className="prec__tagopt__label">{o.label}</span>
                <span className="prec__tagopt__count">{o.count}</span>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}

function prettyDealType(d: string): string {
  return d.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatMoney(amount: number, currency: string | null): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency ?? "USD",
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${amount}`;
  }
}
